/**
 * ConsultationProgress
 * --------------------
 * Where the client is in the loop, and what happens next.
 *
 * An async medical service is mostly waiting, and waiting without a sense of
 * position is what generates "has anything happened?" messages. This turns the
 * consultation's status into a visible position on a track, and — where the
 * server tells us — how long the current step should take.
 *
 * The steps mirror the server's statuses rather than inventing a parallel
 * model, so this cannot drift from what the backend actually does.
 */
import { Box, Stack, Typography, Chip, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';

/** status -> which step is current. */
const STEP_OF = {
  draft_capture: 0,
  awaiting_purchase: 1,
  awaiting_payment: 1,
  awaiting_physician: 1,
  awaiting_capture: 0,
  awaiting_ai: 2,
  ai_preliminary: 3,
  ai_failed: 2,
  confirmed: 4,
  needs_in_person: 4,
};

const KEYS = ['capture', 'choose', 'analysis', 'review', 'done'];

export default function ConsultationProgress({ consultation, report, t, sx }) {
  const status = consultation?.status;
  if (!status || status === 'expired') return null;

  const current = STEP_OF[status] ?? 0;
  const failed = status === 'ai_failed';
  const sla = report?.slaHours;

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: '.68rem',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'secondary.dark',
          }}
        >
          {t('progress.title')}
        </Typography>
        {status === 'ai_preliminary' && sla && (
          <Chip size="small" color="secondary" variant="outlined"
            label={t('progress.slaChip', { hours: sla })} />
        )}
      </Stack>

      <Stack spacing={1.25}>
        {KEYS.map((key, i) => {
          const done = i < current;
          const isNow = i === current;
          return (
            <Stack key={key} direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{ mt: '2px', display: 'flex', color: done
                ? 'success.main'
                : isNow ? 'secondary.main' : 'text.disabled' }}>
                {done
                  ? <CheckCircleIcon sx={{ fontSize: 19 }} />
                  : isNow
                    ? <AutorenewIcon sx={{ fontSize: 19 }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 19 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: isNow ? 800 : 600,
                    color: done || isNow ? 'text.primary' : 'text.disabled',
                    fontSize: '.95rem',
                  }}
                >
                  {t(`progress.step.${key}`)}
                </Typography>
                {isNow && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {failed
                      ? t('progress.failed')
                      : t(`progress.now.${key}`, {
                          name: consultation?.physicianName || '',
                          hours: sla ?? 48,
                        })}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>

      {/* A moving bar only while something is genuinely running server-side. */}
      {status === 'awaiting_ai' && (
        <LinearProgress color="secondary" sx={{ mt: 1.5, borderRadius: 1 }} />
      )}
    </Box>
  );
}
